import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

/**
 * "What needs me right now" — the dashboard's primary job.
 *
 * Roles in Lynk are per-conference, not per-person: the same user can owe a
 * review in one conference, chair a second, and be waiting on a decision as an
 * author in a third. So this is deliberately ONE list rather than role-specific
 * views — the role is metadata on each item ("as reviewer"), not a mode you
 * switch into. A pure author simply sees fewer kinds of row, not a different
 * page.
 *
 * Built entirely from data the dashboard already loads; it issues no queries of
 * its own and changes no business logic.
 */

const DAY = 86400000;

/** Lower sorts first. Overdue work outranks everything, invitations block others. */
const SEVERITY = { overdue: 0, invitation: 1, action: 2, soon: 3, open: 4, info: 5 };

const TONE = {
  overdue: { text: 'text-error', rail: 'bg-error', icon: 'AlertTriangle' },
  invitation: { text: 'text-primary', rail: 'bg-primary', icon: 'UserPlus' },
  action: { text: 'text-accent', rail: 'bg-accent', icon: 'PenLine' },
  soon: { text: 'text-warning', rail: 'bg-warning', icon: 'Clock' },
  open: { text: 'text-muted-foreground', rail: 'bg-border', icon: 'ClipboardCheck' },
  info: { text: 'text-muted-foreground', rail: 'bg-border', icon: 'Info' },
};

const daysFrom = (d) => (d ? Math.ceil((new Date(d).getTime() - Date.now()) / DAY) : null);

function dueLabel(days, t) {
  if (days === null) return null;
  const k = 'userDashboard.needsAttention.due';
  if (days < 0) return t(`${k}.overdue`, { count: Math.abs(days) });
  if (days === 0) return t(`${k}.today`);
  if (days === 1) return t(`${k}.tomorrow`);
  return t(`${k}.left`, { count: days });
}

export default function NeedsAttention({
  reviewerAssignments = [],
  userSubmissions = [],
  pendingInvitations = [],
  chairWorkload = [],
  onAcceptInvitation,
  acceptingInvitationId,
  loading = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('dashboard');
  // Short alias — every key in this component lives under the same parent.
  const k = (suffix, opts) => t(`userDashboard.needsAttention.${suffix}`, opts);

  const items = useMemo(() => {
    const out = [];

    // ── Reviewer invitations: they gate work, so they rank high ──────────
    pendingInvitations?.forEach((inv) => {
      out.push({
        key: `inv-${inv?.id}`,
        severity: 'invitation',
        title: k('invitation.title'),
        detail: inv?.conferenceName || inv?.conferenceShortName,
        context: inv?.invitedByName ? k('invitation.invitedBy', { name: inv.invitedByName }) : null,
        hat: 'reviewer',
        conference: inv?.conferenceShortName || inv?.conferenceName,
        days: null,
        action: {
          label: k('invitation.action'),
          loading: acceptingInvitationId === inv?.id,
          onClick: () => onAcceptInvitation?.(inv?.id),
        },
      });
    });

    // ── Reviews owed ─────────────────────────────────────────────────────
    reviewerAssignments?.filter((a) => a?.isDraft)?.forEach((a) => {
      const days = daysFrom(a?.reviewDeadline);
      const severity = days === null ? 'open' : days < 0 ? 'overdue' : days <= 7 ? 'soon' : 'open';
      const started = a?.status === 'in-progress';
      out.push({
        key: `rev-${a?.reviewId}`,
        severity,
        title: started ? k('review.finish') : k('review.notStarted'),
        detail: a?.paperTitle,
        context: a?.paperNumber ? `#${a.paperNumber}` : null,
        hat: 'reviewer',
        conference: a?.conferenceShortName || a?.conferenceName,
        days,
        action: {
          label: started ? k('review.continue') : k('review.start'),
          onClick: () => navigate(`/streamlined-review-workspace?submission=${a?.submissionId}`),
        },
      });
    });

    // ── Author obligations ───────────────────────────────────────────────
    userSubmissions?.forEach((s) => {
      if (s?.status === 'revision_required') {
        out.push({
          key: `rev-req-${s?.id}`,
          severity: 'action',
          title: k('author.revision'),
          detail: s?.title,
          hat: 'author',
          conference: s?.conference?.shortName || s?.conference?.name,
          days: null,
          action: {
            label: k('author.open'),
            onClick: () => navigate(`/author-review-results-dashboard`),
          },
        });
      }
      // Accepted but no camera-ready uploaded yet — a real, missable deadline.
      if (s?.status === 'accepted' && !s?.cameraReadyFileName) {
        out.push({
          key: `cr-${s?.id}`,
          severity: 'action',
          title: k('author.cameraReady'),
          detail: s?.title,
          hat: 'author',
          conference: s?.conference?.shortName || s?.conference?.name,
          days: null,
          action: {
            label: k('author.upload'),
            onClick: () => navigate('/my-submissions'),
          },
        });
      }
    });

    // ── Chair / organiser: what is blocking the conference ───────────────
    // Aggregated per conference rather than per paper: a chair with 12
    // unassigned papers needs one line saying so, not twelve rows.
    chairWorkload?.forEach((c) => {
      const label = c?.shortName || c?.name;

      if (c?.needsReviewers > 0) {
        out.push({
          key: `chair-assign-${c?.id}`,
          severity: c?.reviewDeadlineDays !== null && c?.reviewDeadlineDays < 0 ? 'overdue' : 'action',
          title: k('chair.needsReviewers', { count: c.needsReviewers }),
          detail: k('chair.needsReviewersDetail'),
          hat: 'chair',
          conference: label,
          days: c?.reviewDeadlineDays ?? null,
          action: {
            label: k('chair.assign'),
            onClick: () => navigate(`/manual-reviewer-assignment-interface?conferenceId=${c?.id}`),
          },
        });
      }

      if (c?.reviewsOutstanding > 0) {
        const days = c?.reviewDeadlineDays ?? null;
        out.push({
          key: `chair-reviews-${c?.id}`,
          severity: days !== null && days < 0 ? 'overdue' : days !== null && days <= 7 ? 'soon' : 'open',
          title: k('chair.reviewsOutstanding', { count: c.reviewsOutstanding }),
          detail: k('chair.reviewsOutstandingDetail'),
          hat: 'chair',
          conference: label,
          days,
          action: {
            label: k('chair.view'),
            onClick: () => navigate(`/conference-management-dashboard?conferenceId=${c?.id}`),
          },
        });
      }

      if (c?.awaitingDecision > 0) {
        out.push({
          key: `chair-decide-${c?.id}`,
          severity: 'action',
          title: k('chair.awaitingDecision', { count: c.awaitingDecision }),
          detail: k('chair.awaitingDecisionDetail'),
          hat: 'chair',
          conference: label,
          days: null,
          action: {
            label: k('chair.decide'),
            // Decisions are made in the conference's submissions table; the separate
            // Decisions screen has been removed.
            onClick: () => navigate(`/conference-management-dashboard?conferenceId=${c?.id}`),
          },
        });
      }
    });

    return out.sort((a, b) => {
      const s = SEVERITY[a.severity] - SEVERITY[b.severity];
      if (s !== 0) return s;
      if (a.days === null) return 1;
      if (b.days === null) return -1;
      return a.days - b.days;
    });
  // `t` matters: without it the memo keeps stale copy after a language switch.
  }, [reviewerAssignments, userSubmissions, pendingInvitations, chairWorkload, acceptingInvitationId, navigate, onAcceptInvitation, t]);

  if (loading) {
    return (
      <section className="bg-card border border-border mb-6">
        <div className="px-5 py-4 border-b border-border">
          <div className="h-4 w-40 bg-muted animate-pulse" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="px-5 py-4 border-b border-border last:border-0 flex gap-3">
            <div className="h-8 w-8 bg-muted animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-3.5 w-1/3 bg-muted animate-pulse" />
              <div className="h-3 w-2/3 bg-muted animate-pulse" />
            </div>
          </div>
        ))}
      </section>
    );
  }

  return (
    <section className="bg-card border border-border mb-6 overflow-hidden">
      <header className="px-4 sm:px-5 py-3.5 border-b border-border flex items-center justify-between gap-4">
        <h2 className="text-sm font-semibold text-foreground">
          {k('title')}
        </h2>
        {items.length > 0 && (
          <span className="text-xs text-muted-foreground tnum">
            {items.length}
          </span>
        )}
      </header>

      {items.length === 0 ? (
        <div className="px-4 sm:px-5 py-10 text-center">
          <Icon name="Check" size={20} className="mx-auto mb-2 text-success" />
          <p className="text-sm font-medium text-foreground">{k('empty.title')}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {k('empty.body')}
          </p>
        </div>
      ) : (
        <ul>
          {items.map((it) => {
            const tone = TONE[it.severity];
            const due = dueLabel(it.days, t);
            return (
              <li
                key={it.key}
                className="relative flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 px-4 sm:px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/40 transition-colors"
              >
                {/* 2px severity rail rather than a coloured card — carries the
                    signal without turning every row into a decorated object.
                    `start-0` (logical) not `left-0`: the rail must hug the
                    reading edge, which flips to the right under RTL. */}
                <span className={`absolute inset-y-0 start-0 w-0.5 ${tone.rail}`} aria-hidden="true" />

                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <Icon name={tone.icon} size={16} className={`${tone.text} mt-0.5 flex-shrink-0`} />

                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground leading-snug">
                      {it.title}
                    </p>
                    {it.detail && (
                      // Wraps to two lines on narrow screens instead of
                      // truncating a paper title into uselessness; still
                      // single-line-truncated once there's room for it.
                      <p className="text-sm text-muted-foreground leading-snug mt-0.5 line-clamp-2 sm:line-clamp-1">
                        {it.context && <span className="mono text-xs me-1.5">{it.context}</span>}
                        {it.detail}
                      </p>
                    )}
                    {/* <bdi> isolates the conference name: titles are usually
                        Latin even in an Arabic UI, and without isolation the
                        bidi algorithm drags the "·" separator to the wrong end
                        of the line. */}
                    <p className="text-xs text-muted-foreground mt-1">
                      <bdi>{it.conference}</bdi>
                      {it.hat && (
                        <span className="opacity-70">
                          {' · '}
                          <bdi>{k(`hat.${it.hat}`)}</bdi>
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Below sm this drops under the text and spreads: due date at
                    the reading edge, action at the far edge — thumb-reachable
                    and never competing with the title for width. `ps-7` keeps
                    it aligned under the text, past the icon gutter. */}
                <div className="flex items-center gap-3 flex-shrink-0 ps-7 sm:ps-0 justify-between sm:justify-end">
                  {due && (
                    <span className={`text-xs tnum ${it.severity === 'overdue' ? 'text-error font-medium' : 'text-muted-foreground'}`}>
                      {due}
                    </span>
                  )}
                  {/* One variant for every row. Severity is already carried
                      twice over — by the coloured rail on the left and by the
                      red "overdue" text beside this button — so varying the
                      button as well encoded it three times and made a list of
                      equivalent obligations look like three different tiers. */}
                  {it.action && (
                    <Button
                      variant="default"
                      size="xs"
                      loading={it.action.loading}
                      onClick={it.action.onClick}
                    >
                      {it.action.label}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
